/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { Check, Info, RotateCcw, RotateCw, Sparkles, X } from "lucide-react";
import { memo, type Dispatch, type MutableRefObject, type SetStateAction } from "react";
import { TableCell } from "../../components/ui/table";
import { theme } from "../../styles/theme/theme";
import { formatCurrencyOrNull, formatWOS } from "../../utils/format";
import { MEASURE_FORMAT } from "../../lib/supplyWalkMeasures";
import { PortalTooltip } from "./PortalTooltip";
import { FALLBACK_SUPPRESSED_KEYS, MEASURE_SIGN, SIGN_COLOR, getMeasureLabel, wosCellBg } from "./constants";
import { s } from "./RetailerSupplyView.styles";
import { MEASURE, maskWOSForEmptyDemand, type ParityRunCell } from "./calculations";
import type { SkuRow as SkuRowType } from "./types";
import { formatForNumberInput, formatGrainValue, sameRounded } from "./utils";

const promoLabelSpacerStyle = css`
  display: inline-block;
  width: 1em;
  margin-right: 0.25rem;
  color: transparent;
  text-align: center;
`;

const promoBubbleStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${theme.colors.blue100};
  color: ${theme.colors.blue600};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background-color: #bfdbfe;
  }
`;

const promoPopoverStyle = css`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 50;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  color: ${theme.colors.foreground};
`;

const detailTotalCellStyle = css`
  ${s.detailTotalCell};
  font-weight: 400;
`;

const detailTotalCellWithFgStyle = css`
  ${s.detailTotalCell};
  font-weight: 400;
  color: ${theme.colors.foreground};
`;

const revertButtonStyle = css`
  margin-left: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  background: transparent;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: ${theme.colors.mutedForeground};
  border-radius: 3px;
  transition: background-color 0.15s, color 0.15s;
  &:hover {
    background: ${theme.colors.muted};
    color: ${theme.colors.foreground};
  }
`;

const parityChipStyle = (anchorLeft: boolean) => css`
  position: absolute;
  bottom: calc(100% + 4px);
  ${anchorLeft ? "left: 0;" : "right: 0;"}
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 4px 6px 4px 8px;
  background: ${theme.colors.background};
  color: ${theme.colors.blue700};
  border: 1px solid ${theme.colors.blue100};
  border-radius: 6px;
  font-size: 0.6875rem;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 20;
  pointer-events: auto;
`;

const parityChipBtnStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: ${theme.colors.mutedForeground};
  border-radius: 3px;
  transition: background-color 0.15s, color 0.15s;
  &:hover {
    background: ${theme.colors.muted};
    color: ${theme.colors.foreground};
  }
`;

const parityChipApplyStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.blue500};
  border: none;
  padding: 2px 4px;
  cursor: pointer;
  color: white;
  border-radius: 3px;
  transition: background-color 0.15s;
  &:hover {
    background: ${theme.colors.blue700};
  }
`;

const parityGhostCellStyle = css`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 0.5rem;
  font-size: 0.8125rem;
  font-style: italic;
  font-variant-numeric: tabular-nums;
  color: ${theme.colors.blue700};
  pointer-events: none;
  z-index: 2;
  background: ${theme.colors.blue50};
  /* When the user focuses the underlying input, get out of the way so they
     can see / replace the value they're typing. */
  input:focus ~ & {
    display: none;
  }
`;

const clearButtonStyle = css`
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border: none;
  background: ${theme.colors.clearBtnBg};
  padding: 0;
  border-radius: 50%;
  cursor: pointer;
  color: ${theme.colors.white};
  transition: background-color 0.15s;
  &:hover {
    background: ${theme.colors.gray400};
  }
`;

const placeholderItalicStyle = css`
  &::placeholder {
    color: ${theme.colors.gray400};
    font-style: italic;
  }
`;

const inputOverriddenPaddingStyle = css`
  padding-right: 1.375rem;
`;

const dashCellStyle = css`
  ${s.detailValueCell};
  font-weight: 400;
  background-color: transparent;
`;

export interface SkuDetailRowProps {
  row: SkuRowType;
  mk: string;
  /** Slice of editedMeasures for this row (undefined when row is untouched). */
  editsForRow: Record<string, number[]> | undefined;
  /** Slice of clearedCells for this row (undefined when row has no clears). */
  clearsForRow: Set<number> | undefined;
  /** Original (pre-overlay) measures for this row — used to flag user-typed
   *  cells that diverge from the saved override (drives editInputEdited bg). */
  originalMeasuresForRow: Record<string, number[]> | undefined;
  /** True when the parent holds a pre-revert snapshot for this row, i.e. the
   *  user just refresh-cleared the row and hasn't saved or made fresh edits.
   *  Surfaces a Restore button in place of the revert button. */
  canRestore: boolean;
  visiblePeriodSet: Set<string>;
  periodColumns: string[];
  pastPeriodSet: Set<string>;
  /** Period index of the open promo popover for this row, or null. */
  activePromoPeriodIdx: number | null;
  /** Active parity-run hint for THIS row, or null when there is none. */
  parityHintForRow: { editedWeekIdx: number; cells: ParityRunCell[] } | null;
  /** Stable handler refs — created via ref shim in the parent so identity
   *  survives edit-state churn and the memo's shallow compare can skip render. */
  handleSimEdit: (row: SkuRowType, weekIdx: number, newValue: number) => void;
  handleClearEdit: (row: SkuRowType, weekIdx: number) => void;
  handleRevertRow: (row: SkuRowType) => void;
  handleRestoreRow: (row: SkuRowType) => void;
  handleApplyParityHint: (row: SkuRowType) => void;
  dismissParityHint: () => void;
  setActivePromo: Dispatch<SetStateAction<{ rowId: string; periodIdx: number } | null>>;
  setPromoAnchor: Dispatch<SetStateAction<HTMLElement | null>>;
  setClearedCells: Dispatch<SetStateAction<Map<string, Set<number>>>>;
  cellEditModeRef: MutableRefObject<boolean>;
}

function SkuDetailRowImpl({
  row,
  mk,
  editsForRow,
  clearsForRow,
  originalMeasuresForRow,
  canRestore,
  visiblePeriodSet,
  periodColumns,
  pastPeriodSet,
  activePromoPeriodIdx,
  parityHintForRow,
  handleSimEdit,
  handleClearEdit,
  handleRevertRow,
  handleRestoreRow,
  handleApplyParityHint,
  dismissParityHint,
  setActivePromo,
  setPromoAnchor,
  setClearedCells,
  cellEditModeRef,
}: SkuDetailRowProps) {
  if (mk === "promotions") {
    return (
      <>
        <TableCell css={s.detailSpacerCell0} />
        <TableCell css={s.detailSpacerCell1} />
        <TableCell css={s.detailSpacerCell2} />
        <TableCell css={s.detailLabelCell(false)}>
          <span css={promoLabelSpacerStyle}>{" "}</span>
          Promotions
        </TableCell>
        {row.promotions.map((promo, ci) => {
          if (!visiblePeriodSet.has(periodColumns[ci])) return null;
          const isOpen = activePromoPeriodIdx === ci;
          return (
            <TableCell
              key={ci}
              css={css`
                ${s.detailValueCell};
                text-align: center;
                position: relative;
                background-color: ${pastPeriodSet.has(periodColumns[ci])
                  ? theme.colors.pastPeriodBg
                  : "transparent"};
              `}
            >
              {promo != null && (
                <>
                  <span
                    css={promoBubbleStyle}
                    onClick={(e) => {
                      const target = e.currentTarget as HTMLElement;
                      if (isOpen) {
                        setActivePromo(null);
                        setPromoAnchor(null);
                      } else {
                        setActivePromo({ rowId: row.id, periodIdx: ci });
                        setPromoAnchor(target);
                      }
                    }}
                  >
                    P
                  </span>
                  {isOpen && (
                    <div css={promoPopoverStyle}>
                      {promo.split(", ").map((p, i) => (
                        <div key={i}>{p}</div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </TableCell>
          );
        })}
        <TableCell css={detailTotalCellStyle} />
      </>
    );
  }

  const effectiveMeasures = editsForRow ?? row.measures;
  const isEnding = mk === "ending_inventory";
  const isEditable = mk === "simulated_shipments_placed_planner_override";
  const isAtomic = mk === "simulated_shipments_placed_atomic";
  // For the two Forecast Shipments rows, past-period cells display the
  // historical actuals (open_shipments_placed) instead of the always-zero
  // engine forecast. Consolidates what used to be a separate "Actual
  // Shipments" walk row into the past columns of the forecast rows.
  const rawValues = effectiveMeasures[mk] ?? [];
  const isWOS = mk === "ending_days_of_supply";
  const values =
    (isAtomic || isEditable)
      ? rawValues.map((v, i) =>
          pastPeriodSet.has(periodColumns[i])
            ? (row.measures.open_shipments_placed?.[i] ?? 0)
            : v,
        )
      : isWOS
        ? maskWOSForEmptyDemand(rawValues, effectiveMeasures.forecast_sales ?? [])
        : rawValues;
  const total = values.reduce((a, v) => a + v, 0);
  const sign = MEASURE_SIGN[mk];
  // CHO-459: forecast_revenue renders as currency; other measures keep grain-aware integer formatting.
  const formatVal = isWOS
    ? formatWOS
    : MEASURE_FORMAT[mk] === "currency"
      ? (v: number) => formatCurrencyOrNull(v)
      : formatGrainValue;

  const rowHasAnyDiff = (() => {
    if (!isAtomic && !isEditable) return false;
    const atomicVals = row.measures.simulated_shipments_placed_atomic ?? [];
    const overrideVals = effectiveMeasures.simulated_shipments_placed_planner_override ?? [];
    const n = Math.max(atomicVals.length, overrideVals.length);
    for (let i = 0; i < n; i++) {
      if (clearsForRow?.has(i)) continue;
      if (atomicVals[i] !== undefined && !sameRounded(atomicVals[i], overrideVals[i])) return true;
    }
    return false;
  })();

  return (
    <>
      <TableCell css={s.detailSpacerCell0} />
      <TableCell css={s.detailSpacerCell1} />
      <TableCell css={s.detailSpacerCell2} />
      <TableCell css={s.detailLabelCell(isEnding)}>
        <span
          css={css`
            display: inline-block;
            width: 1em;
            margin-right: 0.25rem;
            color: ${sign ? SIGN_COLOR[sign] : "transparent"};
            text-align: center;
          `}
        >
          {sign ?? " "}
        </span>
        {getMeasureLabel(mk, "Week")}
        {mk === "starting_inventory" && (
          <PortalTooltip text={`IOH source: ${row.iohSource ?? "unknown"}`}>
            <span css={s.iohSourceInfoIcon}>
              <Info size={12} />
            </span>
          </PortalTooltip>
        )}
        {isEditable &&
          (() => {
            const atomic = row.measures.simulated_shipments_placed_atomic ?? [];
            const override = effectiveMeasures.simulated_shipments_placed_planner_override ?? [];
            const n = Math.max(atomic.length, override.length);
            let hasEdits = false;
            for (let i = 0; i < n; i++) {
              if (clearsForRow?.has(i)) continue;
              if (atomic[i] === undefined) continue;
              if (atomic[i] !== override[i]) {
                hasEdits = true;
                break;
              }
            }
            if (hasEdits) {
              return (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRevertRow(row);
                  }}
                  title="Revert all edits in this row"
                  css={revertButtonStyle}
                >
                  <RotateCcw size={12} />
                </button>
              );
            }
            if (canRestore) {
              return (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRestoreRow(row);
                  }}
                  title="Restore the edits you just cleared"
                  css={revertButtonStyle}
                >
                  <RotateCw size={12} />
                </button>
              );
            }
            return null;
          })()}
      </TableCell>

      {values.map((v, ci) => {
        if (!visiblePeriodSet.has(periodColumns[ci])) return null;

        // CHO-610: NetSuite-fallback customers have no real inventory
        // anchor, so the inventory walk and Alloy-POS actual sales for
        // these rows are meaningless. The engine emits null
        // (logic_ui_supply_walk.py); render an explicit dash so planners
        // see "no data" instead of zeros.
        if (
          row.iohSource === "netsuite_fallback" &&
          FALLBACK_SUPPRESSED_KEYS.has(mk)
        ) {
          return (
            <TableCell key={ci} css={dashCellStyle}>
              —
            </TableCell>
          );
        }

        let bgOverride = "";
        if (isWOS) {
          bgOverride = wosCellBg(v, row.targetWOS);
        }
        if (mk === "simulated_shipments_received" && v > 0) {
          bgOverride = theme.colors.green200;
        }
        const isPastPeriod = pastPeriodSet.has(periodColumns[ci]);
        const isActiveSales =
          (mk === "actual_sales" && isPastPeriod) ||
          (mk === "forecast_sales" && !isPastPeriod);

        if (isEditable) {
          const isPast = pastPeriodSet.has(periodColumns[ci]);
          const engineVal = row.measures.simulated_shipments_placed_atomic?.[ci];
          const suggestedCell = parityHintForRow?.cells.find((c) => c.weekIdx === ci);
          const isEditedHintCell = parityHintForRow?.editedWeekIdx === ci;
          const zeroCount = parityHintForRow?.cells.length ?? 0;
          return (
            <TableCell key={ci} css={s.detailEditCell(isPast)}>
              {isPast ? (
                formatVal(v)
              ) : (
                (() => {
                  const hasAtomicRow = !!row.measures.simulated_shipments_placed_atomic;
                  const isOverridden =
                    hasAtomicRow &&
                    engineVal !== undefined &&
                    !sameRounded(engineVal, v) &&
                    !clearsForRow?.has(ci);
                  const showAsPlaceholder = !rowHasAnyDiff;
                  const origVal =
                    originalMeasuresForRow?.simulated_shipments_placed_planner_override?.[ci];
                  return (
                    <>
                      <input
                        css={[
                          origVal !== undefined && v !== origVal ? s.editInputEdited : s.editInput,
                          isOverridden && inputOverriddenPaddingStyle,
                          showAsPlaceholder && placeholderItalicStyle,
                        ]}
                        type="number"
                        defaultValue={showAsPlaceholder ? "" : formatForNumberInput(v)}
                        placeholder={showAsPlaceholder ? formatForNumberInput(v) : undefined}
                        key={`${row.id}-${ci}-${showAsPlaceholder ? `ph-${v}` : v}`}
                        min={0}
                        data-edit-cell
                        data-row={row.id}
                        data-col={ci}
                        onFocus={(e) => {
                          cellEditModeRef.current = false;
                          e.target.select();
                        }}
                        onDoubleClick={() => {
                          cellEditModeRef.current = true;
                        }}
                        onBlur={(e) => {
                          cellEditModeRef.current = false;
                          const raw = e.target.value.trim();
                          if (raw === "" || isNaN(parseInt(raw))) {
                            if (isOverridden) handleClearEdit(row, ci);
                            return;
                          }
                          const newVal = Math.max(0, parseInt(raw));
                          if (formatForNumberInput(newVal) !== formatForNumberInput(v)) {
                            setClearedCells((prev) => {
                              const set = prev.get(row.id);
                              if (!set?.has(ci)) return prev;
                              const next = new Map(prev);
                              const newSet = new Set(set);
                              newSet.delete(ci);
                              if (newSet.size === 0) next.delete(row.id);
                              else next.set(row.id, newSet);
                              return next;
                            });
                            handleSimEdit(row, ci, newVal);
                          }
                        }}
                        onKeyDown={(e) => {
                          const el = e.target as HTMLInputElement;
                          if (e.key === "F2") {
                            e.preventDefault();
                            cellEditModeRef.current = true;
                            el.setSelectionRange(el.value.length, el.value.length);
                            return;
                          }
                          if (e.key === "Escape") {
                            e.preventDefault();
                            if (cellEditModeRef.current) {
                              cellEditModeRef.current = false;
                              el.select();
                            } else {
                              el.value = showAsPlaceholder ? "" : formatForNumberInput(v);
                              el.blur();
                            }
                            return;
                          }
                          if (e.key === "Enter") {
                            e.preventDefault();
                            el.blur();
                            return;
                          }
                          if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                            if (cellEditModeRef.current) return;
                            e.preventDefault();
                            el.blur();
                            const tr = el.closest("tr");
                            if (!tr) return;
                            const inputs = Array.from(
                              tr.querySelectorAll<HTMLInputElement>("input[type=number]"),
                            );
                            const idx = inputs.indexOf(el);
                            const direction = e.key === "ArrowLeft" ? -1 : 1;
                            const next = inputs[idx + direction];
                            if (next) next.focus();
                            return;
                          }
                          if (e.key === "Tab") {
                            e.preventDefault();
                            el.blur();
                            const tr = el.closest("tr");
                            if (!tr) return;
                            const inputs = Array.from(
                              tr.querySelectorAll<HTMLInputElement>("input[type=number]"),
                            );
                            const idx = inputs.indexOf(el);
                            const direction = e.shiftKey ? -1 : 1;
                            const next = inputs[idx + direction];
                            if (next) next.focus();
                            return;
                          }
                        }}
                      />
                      {isOverridden && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClearEdit(row, ci);
                          }}
                          title="Clear override"
                          css={clearButtonStyle}
                        >
                          <X size={8} strokeWidth={2.5} />
                        </button>
                      )}
                      {suggestedCell && (
                        <span css={parityGhostCellStyle}>
                          {formatVal(suggestedCell.value)}
                        </span>
                      )}
                      {isEditedHintCell && parityHintForRow && parityHintForRow.cells.length > 0 && (
                        <div
                          css={parityChipStyle(ci < periodColumns.length / 2)}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          <Sparkles size={11} />
                          <span>{`Adjust next ${zeroCount} week${zeroCount === 1 ? "" : "s"}?`}</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApplyParityHint(row);
                            }}
                            title="Apply zero-run"
                            css={parityChipApplyStyle}
                          >
                            <Check size={12} strokeWidth={3} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              dismissParityHint();
                            }}
                            title="Dismiss suggestion"
                            css={parityChipBtnStyle}
                          >
                            <X size={12} strokeWidth={2.5} />
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()
              )}
            </TableCell>
          );
        }

        if (mk === "actual_sales" && !isPastPeriod) {
          return (
            <TableCell key={ci} css={dashCellStyle}>
              —
            </TableCell>
          );
        }

        const effOverride = effectiveMeasures.simulated_shipments_placed_planner_override?.[ci];
        const isAtomicCellDiff =
          isAtomic &&
          !isPastPeriod &&
          effOverride !== undefined &&
          !sameRounded(effOverride, v) &&
          !clearsForRow?.has(ci);
        const isDelta = mk === MEASURE.FORECAST_SHIPMENTS_DELTA;
        const cellColor = isDelta
          ? v > 0
            ? theme.colors.status.success
            : v < 0
              ? theme.colors.status.error
              : theme.colors.foreground
          : isAtomic && rowHasAnyDiff && !isPastPeriod
            ? theme.colors.gray400
            : isActiveSales
              ? theme.colors.red600
              : theme.colors.foreground;
        const cellBg = isAtomicCellDiff
          ? theme.colors.gray200
          : isPastPeriod
            ? theme.colors.pastPeriodBg
            : bgOverride || "transparent";
        return (
          <TableCell
            key={ci}
            css={css`
              ${s.detailValueCell};
              color: ${cellColor};
              font-weight: ${isActiveSales ? 500 : 400};
              background-color: ${cellBg};
            `}
          >
            {formatVal(v)}
          </TableCell>
        );
      })}

      <TableCell
        css={
          mk === MEASURE.FORECAST_SHIPMENTS_DELTA
            ? css`
                ${s.detailTotalCell};
                font-weight: 400;
                color: ${total > 0 ? theme.colors.status.success : total < 0 ? theme.colors.status.error : theme.colors.foreground};
              `
            : detailTotalCellWithFgStyle
        }
      >
        {row.iohSource === "netsuite_fallback" && FALLBACK_SUPPRESSED_KEYS.has(mk)
          ? "—"
          : isWOS
            ? formatWOS(total / Math.max(1, values.length))
            : formatVal(total)}
      </TableCell>
    </>
  );
}

export const SkuDetailRow = memo(SkuDetailRowImpl);
